/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Chapter content tree container component.
 *
 * @file ChapterContentTree.jsx
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import { useChapterContent } from './ChapterContentContext';
import {
  ChapterTreeExpansionProvider,
  useChapterTreeExpansion
} from './useChapterTreeExpansion';
import { getScrollContainerStyle } from './chapterContentTreeUtils';
import ChapterContentTreeRow from './ChapterContentTreeRow';
import { ChapterContentTreeStylesRoot, classes } from './styles/chapterContentTreeStyles';

const ChapterContentTree = ({
  selectedNodes,
  onToggleNode,
  style: styleProp
}) => {
  const {
    nodes,
    textInfo,
    variant,
    style: contextStyle
  } = useChapterContent();

  const expansion = useChapterTreeExpansion({
    nodes,
    selectedNodes,
    onToggleNode
  });

  const scrollStyle = getScrollContainerStyle(styleProp || contextStyle || {});

  const renderScheduleInstruction = () => {
    if (variant !== 'schedule') {
      return null;
    }

    return (
      <Typography component="p" className={classes.instruction}>
        {textInfo.scheduleInstruction}
      </Typography>
    );
  };

  const renderTreeRows = () => nodes.map((node) => (
    <ChapterContentTreeRow key={node.id} node={node} level={0} />
  ));

  const renderScrollContainer = () => (
    <div className={classes.scrollContainer} style={scrollStyle}>
      {renderTreeRows()}
    </div>
  );

  return (
    <ChapterTreeExpansionProvider value={expansion}>
      <ChapterContentTreeStylesRoot className={classes.treeRoot}>
        {renderScheduleInstruction()}
        {renderScrollContainer()}
      </ChapterContentTreeStylesRoot>
    </ChapterTreeExpansionProvider>
  );
};

ChapterContentTree.propTypes = {
  selectedNodes: PropTypes.array,
  onToggleNode: PropTypes.func.isRequired,
  style: PropTypes.object
};

ChapterContentTree.defaultProps = {
  selectedNodes: [],
  style: null
};

export default ChapterContentTree;
